import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ActionIcon, Badge, Checkbox, Group, Paper, Stack, Text } from '@mantine/core';
import { IconEye } from '@tabler/icons-react';
import type { LinkedIssue } from '../../../stores/types';
import { getDprIssueTrackerTicketUrl } from '../../../utils/config';
import { isClosedIssue, pendingWithDisplayName } from '../../../utils/linkedIssueHelpers';
import { openPortalUrl } from '../../../utils/portalNavigation';

/** Open ticket in portal-v2 (syncs cookies + projectId via openPortalUrl). */
const SHOW_ISSUE_VIEW_ICON = true;

export const TASK_DETAILS_LINKED_ISSUES_SECTION_ID = 'task-details-linked-issues';

const CLOSED_STATUS_BADGE_STYLES = {
  root: {
    color: '#065F46',
    backgroundColor: '#D1FAE5',
    // border: '1px solid #6EE7B7'
  },
};

export interface TaskLinkedIssuesSectionProps {
  linkedIssues?: LinkedIssue[] | null;
  openIssueCount?: number | null;
  /** Increment to scroll this section into view (e.g. from Gantt issue-count badge). */
  scrollIntoViewRequest?: number;
}

function statusChipColor(statusCategory: string): string {
  const key = statusCategory.trim().toLowerCase();
  if (key === 'wip') return 'yellow';
  if (key === 'done' || key === 'close') return 'teal';
  if (key === 'blocked') return 'red';
  return 'gray';
}

/**
 * Read-only linked DPR issues / hindrances for the selected task.
 */
export function TaskLinkedIssuesSection({
  linkedIssues,
  openIssueCount,
  scrollIntoViewRequest = 0,
}: TaskLinkedIssuesSectionProps) {
  const rows = Array.isArray(linkedIssues) ? linkedIssues : [];
  const [showAllIssues, setShowAllIssues] = useState(false);
  const sectionRef = useRef<HTMLDivElement | null>(null);

  const visibleRows = useMemo(
    () => (showAllIssues ? rows : rows.filter((issue) => !isClosedIssue(issue))),
    [rows, showAllIssues],
  );

  useEffect(() => {
    if (!scrollIntoViewRequest || rows.length === 0) return;
    let cancelled = false;
    const scroll = () => {
      if (cancelled) return;
      sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    // Early attempt + retry after drawer/panel open transition (~200ms).
    const t1 = window.setTimeout(scroll, 50);
    const t2 = window.setTimeout(scroll, 280);
    return () => {
      cancelled = true;
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [scrollIntoViewRequest, rows.length]);

  if (rows.length === 0) return null;

  const count = Number(openIssueCount ?? 0);

  return (
    <Stack
      ref={sectionRef}
      id={TASK_DETAILS_LINKED_ISSUES_SECTION_ID}
      gap="sm"
      mt="lg"
      pt="xs"
    >
      <Group justify="space-between" align="center" gap="md" wrap="wrap">
        <Group gap={6} align="center">
          <Text size="sm" fw={600}>
            Issues / Hindrances
          </Text>
          {count > 0 ? (
            <>
              <Text size="sm" c="dimmed" aria-hidden>
                ·
              </Text>
              <Text size="xs" c="dimmed" title={`${count} open issue${count === 1 ? '' : 's'}`}>
                {count} Blocking
              </Text>
            </>
          ) : null}
        </Group>

        <Checkbox
          size="xs"
          label="Show all issues"
          checked={showAllIssues}
          onChange={(e) => setShowAllIssues(e.currentTarget.checked)}
          styles={{
            label: { fontSize: 12, color: 'var(--mantine-color-dimmed)' },
          }}
        />
      </Group>

      <Stack gap="sm">
        {visibleRows.map((issue) => {
          const viewUrl = SHOW_ISSUE_VIEW_ICON ? getDprIssueTrackerTicketUrl(issue.ticketId) : '';
          const closed = isClosedIssue(issue);
          return (
            <Paper
              key={issue.ticketId}
              withBorder
              radius="md"
              p="sm"
              style={{ background: 'var(--mantine-color-body)' }}
            >
              <Group justify="space-between" align="flex-start" gap="xs" wrap="nowrap">
                <Stack gap={6} style={{ flex: 1, minWidth: 0 }}>
                  <Group gap="xs" wrap="wrap">
                    <Badge
                      size="sm"
                      variant="light"
                      color={closed ? undefined : statusChipColor(issue.statusCategory)}
                      styles={closed ? CLOSED_STATUS_BADGE_STYLES : undefined}
                    >
                      {issue.status || issue.statusCategory || '—'}
                    </Badge>
                    {issue.currentlyAffected ? (
                      <Text size="xs" c="red" fw={600}>
                        This issue is causing delay
                      </Text>
                    ) : null}
                  </Group>

                  <Text size="sm" fw={500} style={{ lineHeight: 1.35 }}>
                    {issue.title || '—'}
                  </Text>

                  <Group gap="md" wrap="wrap">
                    {issue.category ? (
                      <Text size="xs" c="dimmed">
                        {issue.category}
                      </Text>
                    ) : null}
                    <Text size="xs" c="dimmed">
                      Pending with {pendingWithDisplayName(issue.pendingWith)}
                    </Text>
                  </Group>
                </Stack>

                {viewUrl ? (
                  <ActionIcon
                    component="a"
                    href={viewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="subtle"
                    color="gray"
                    size="sm"
                    aria-label={`View issue ${issue.ticketId}`}
                    title="View issue"
                    onClick={(e) => {
                      e.preventDefault();
                      openPortalUrl('requests/dprIssueTrackerProcess', {
                        query: { ticketId: issue.ticketId },
                      });
                    }}
                  >
                    <IconEye size={16} stroke={1.75} />
                  </ActionIcon>
                ) : null}
              </Group>
            </Paper>
          );
        })}
      </Stack>
    </Stack>
  );
}
